import { ReactElement, useState } from 'react';
import { MdAdd } from 'react-icons/md';
import {
  OnboardingMultiDocument,
  OnboardingStoreType,
  OracleOnboardingMultiDocumentSectionKey
} from '@blisspointmedia/bpm-types/dist/ClientOracleOnboarding';
import { AlertDialog } from '@Components/AlertDialog';
import { Intent } from '@Components/designConstants';
import { useSetError } from '@src/redux/modals';
import {
  fetchMultiDocumentDownloadUrl,
  removeMultiDocument
} from '../onboarding-multi-document-api';
import { MultiDocumentBody, MultiDocumentModalCopy } from '../multi-document-constants';
import { useEditorName } from '../useEditorName';
import { useInvalidateMultiDocuments, useMultiDocuments } from '../useMultiDocuments';
import { useOpenDocument } from '../useOpenDocument';
import { KebabMenu } from './KebabMenu';
import { MultiDocumentFlow } from './MultiDocumentFlow';
import { MultiDocumentList } from './MultiDocumentList';
import { SectionCard } from './SectionCard';

interface MultiDocumentSectionConfig {
  key: OracleOnboardingMultiDocumentSectionKey;
  label: string;
  icon: ReactElement;
  addButtonLabel: string;
  footerCopy: string;
  // Lowercase, for the remove dialog and its error.
  itemNoun: string;
  removeBody: string;
  modal: MultiDocumentModalCopy;
  body: MultiDocumentBody;
}

interface MultiDocumentSectionCardProps {
  company: string;
  store: OnboardingStoreType;
  section: MultiDocumentSectionConfig;
}

// No `doc`: adding. A `doc`: editing it.
type EditorState = { doc?: OnboardingMultiDocument } | null;

export const MultiDocumentSectionCard = ({
  company,
  store,
  section
}: MultiDocumentSectionCardProps) => {
  const { data, isLoading, isError } = useMultiDocuments(company, store, section.key);
  const [editor, setEditor] = useState<EditorState>(null);
  const [removing, setRemoving] = useState<OnboardingMultiDocument | null>(null);
  // Hidden from the list until the removal and the refetch finish, so it cannot be removed twice.
  const [removingIds, setRemovingIds] = useState<ReadonlySet<number>>(new Set());
  const openDocument = useOpenDocument();
  const setError = useSetError();
  const invalidateDocuments = useInvalidateMultiDocuments();
  const editorName = useEditorName();

  // Removes from the document's own store.
  const remove = async (doc: OnboardingMultiDocument) => {
    setRemovingIds(ids => new Set(ids).add(doc.id));
    try {
      await removeMultiDocument({
        company,
        store: doc.store,
        documentId: doc.id,
        expectedVersionId: doc.versionId,
        editorName
      });
    } catch (error) {
      setError({
        message: `Couldn't remove the ${section.itemNoun}: ${error.message}`,
        reportError: error
      });
    } finally {
      await invalidateDocuments(company, doc.store, section.key);
      setRemovingIds(ids => {
        const next = new Set(ids);
        next.delete(doc.id);
        return next;
      });
    }
  };

  return (
    <SectionCard
      icon={section.icon}
      label={section.label}
      rightActions={
        <KebabMenu
          items={[{ value: 'add', label: section.addButtonLabel, leadingContent: <MdAdd /> }]}
          onSelect={() => setEditor({})}
        />
      }
    >
      <MultiDocumentList
        documents={(data?.documents ?? []).filter(doc => !removingIds.has(doc.id))}
        isLoading={isLoading}
        isError={isError}
        body={section.body}
        addButtonLabel={section.addButtonLabel}
        footerCopy={section.footerCopy}
        onAdd={() => setEditor({})}
        onOpen={doc =>
          openDocument(() =>
            fetchMultiDocumentDownloadUrl({ company, store: doc.store, documentId: doc.id })
          )
        }
        onEdit={doc => setEditor({ doc })}
        onRemove={setRemoving}
      />
      {editor && (
        <MultiDocumentFlow
          key={editor.doc?.id ?? 'new'}
          company={company}
          store={store}
          sectionKey={section.key}
          copy={section.modal}
          doc={editor.doc}
          onClose={() => setEditor(null)}
        />
      )}
      <AlertDialog
        intent={Intent.ERROR}
        show={removing !== null}
        onHide={() => setRemoving(null)}
        title={`Remove this ${section.itemNoun}?`}
        body={section.removeBody}
        primaryAction={{ label: 'Remove', onClick: () => removing && remove(removing) }}
        secondaryAction={{ onClick: () => setRemoving(null) }}
      />
    </SectionCard>
  );
};
